import React from 'react';
import { I18n } from 'react-redux-i18n';

import { I18N_BASE } from '../../constants';
import { ActiveDecisionSummary } from '../../models';
import { formatAge } from '../../helpers/formatAge';
import CmButton from '../../components/primitives/CmButton';
import styles from './review.module.css';

export interface ResetBannerProps {
  decision: ActiveDecisionSummary | null;
  canReset: boolean;
  onReset: () => void;
  now?: number;
  testId?: string;
}

export function ResetBanner({
  decision,
  canReset,
  onReset,
  now,
  testId,
}: ResetBannerProps): React.ReactElement | null {
  if (!decision || !canReset) return null;

  const age = formatAge(decision.decidedAt, now ?? Date.now());
  const ageText = I18n.t(age.i18nKey, { value: age.value });
  // Map the raw enum to a past-tense verb sourced from i18n. Avoids rendering
  // "this block 5 minutes ago" (the raw enum) inside a sentence template.
  const decisionVerb = I18n.t(
    `${I18N_BASE}.detail.resetBanner.decisionVerb.${decision.decision}`,
  );

  // Auto decisions read "AI auto-blocked …" with an Auto badge; human
  // decisions read "{reviewer} blocked …" plus the agreement note (which is
  // meaningless for auto decisions since the AI is the decider).
  const message = decision.isAutomated
    ? I18n.t(`${I18N_BASE}.detail.resetBanner.autoMessage`, {
        decision: decisionVerb,
        age: ageText,
      })
    : I18n.t(`${I18N_BASE}.detail.resetBanner.message`, {
        decision: decisionVerb,
        reviewer:
          decision.reviewerName ?? I18n.t(`${I18N_BASE}.general.unknownReviewer`),
        age: ageText,
      });

  // Reverting a Block actually undoes the spam cascade (restores pages,
  // re-enables accounts), so it gets the stronger "Revert" label. Resetting an
  // Approve is just a status reset.
  const buttonLabel = I18n.t(
    decision.decision === 'block'
      ? `${I18N_BASE}.detail.resetBanner.revert`
      : `${I18N_BASE}.detail.resetBanner.reset`,
  );

  return (
    <div
      className={`${styles.banner} ${styles.bannerInfo} ${styles.bannerRow}`}
      data-testid={testId}
      data-banner="reset"
      data-automated={decision.isAutomated}
    >
      <div>
        <div className={styles.bannerHeadline}>
          {decision.isAutomated ? (
            <span className={styles.autoBadge} data-testid={testId ? `${testId}-auto` : undefined}>
              {I18n.t(`${I18N_BASE}.detail.resetBanner.autoBadge`)}
            </span>
          ) : null}
          {message}
        </div>
        {decision.isAutomated ? null : (
          <div
            className={styles.bannerSubtle}
            data-testid={testId ? `${testId}-agreement` : undefined}
          >
            {I18n.t(
              decision.agreedWithAi
                ? `${I18N_BASE}.detail.resetBanner.agreedWithAi`
                : `${I18N_BASE}.detail.resetBanner.disagreedWithAi`,
            )}
          </div>
        )}
      </div>
      <CmButton variant="ghost" onClick={onReset} testId="reset-banner-button">
        {buttonLabel}
      </CmButton>
    </div>
  );
}

export default ResetBanner;
